import React from 'react';
import { Body, Dialog, TextInput } from '@sato/components';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { parkingApi } from '@api/parking.api';
import { PARKING_WATCH_QUERY_KEY } from '@hooks/useParkingWatchForSelectedApartment';
import { LanguageKey } from '@models/Language';
import { ParkingWatch } from '@models/parking.types';
import { useDialog } from '@providers/DialogProvider';
import { apartmentStore } from '@stores/apartmentStore';
import { localizationStore, t } from '@stores/localization';
import { userStore } from '@stores/userStore';

interface ParkingSpaceWatchDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ParkingSpaceWatchDialog: React.FC<ParkingSpaceWatchDialogProps> =
  observer(({ isOpen, onClose }) => {
    const language =
      localizationStore.language.toUpperCase() as Uppercase<LanguageKey>;

    const { openDialog } = useDialog();
    const queryClient = useQueryClient();

    const handleActionButtonClick = async () => {
      try {
        const parkingAreas =
          apartmentStore.selectedApartment?.realestate?.parkingAreas;

        if (!parkingAreas) {
          throw new Error('No parking areas found');
        }

        const email = userStore.user?.email;
        const firstName = userStore.user?.firstName;
        const lastName = userStore.user?.lastName;
        const realEstateName =
          apartmentStore.selectedApartment?.realestate.name;

        const parkingAreaIds = parkingAreas.map((area) => area.id);

        if (!email) {
          throw new Error('No email found');
        }

        const parkingSpaceWatchName = `${t('parking.spaceWatch')}: ${firstName} ${lastName} / ${realEstateName}`;

        const parkingSpaceWatchParams = {
          name: parkingSpaceWatchName,
          emails: [email],
          parkingAreaIds,
          language,
        };

        const response = await parkingApi.createParkingWatch(
          parkingSpaceWatchParams
        );

        // Update the query data so that the UI updates without refetching
        queryClient.setQueryData<ParkingWatch[]>(
          PARKING_WATCH_QUERY_KEY,
          (oldData) => [
            ...(oldData ?? []),
            {
              ...response.data,
              ...parkingSpaceWatchParams,
              createdOn: new Date().toISOString(),
            },
          ]
        );

        onClose();
        // Open confirmation dialog
        openDialog({
          heading: t('parking.thank_you_for_ordering_space_watch'),
          icon: 'car',
          body: t('parking.space_watch_info_message'),
          actionButton: {
            children: t('close'),
          },
        });
      } catch (error) {
        onClose();
        // Open error dialog
        openDialog({
          heading: t('parking.parking_watch_error_title'),
          icon: 'car',
          body: t('parking.parking_watch_error_message'),
          actionButton: {
            children: t('close'),
          },
        });
      }
    };

    return (
      <Dialog
        isOpen={isOpen}
        onOpenChange={(value) => !value && onClose()}
        heading={t('parking.spaceWatch')}
        icon="car"
        body={
          <>
            <Body className="mb-5">{t('parking.orderParkingSpaceWatch')}</Body>

            <TextInput
              name="email"
              type="email"
              label={t('email')}
              value={userStore.user?.email || ''}
              isReadOnly
            />
          </>
        }
        actionButton={{
          children: t('parking.order_notifications'),
          onClick: handleActionButtonClick,
        }}
        backButton={{
          children: t('cancel'),
        }}
      />
    );
  });
